import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page } from "@/components/layout/AppShell";

function GoogleButton() {
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/you";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return <button type="button" onClick={go} className="btn-ghost w-full" data-testid="google-login-btn">Continue with Google</button>;
}

function AuthFrame({ title, sub, children }: any) {
  return (
    <Page className="max-w-md">
      <p className="eyebrow mb-3">Shufflar · Readers, creators & community</p>
      <h1 className="text-4xl sm:text-5xl font-semibold leading-none mb-2">{title}</h1>
      <p className="text-sm text-[#5E5C56] mb-8">{sub}</p>
      <div className="card-tactile p-6 sm:p-8">{children}</div>
    </Page>
  );
}

export default function Login() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/login", form);
      if (data.restricted) { sessionStorage.setItem("tt_appeal", data.appeal_token); nav("/suspended"); return; }
      setSession(data.user, data.token);
      nav(sp.get("next") || "/");
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <AuthFrame title="Welcome back." sub="Sign in to your readings, library and channel.">
      <form onSubmit={submit} className="space-y-4">
        <input className="input-ed" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="login-email" />
        <input className="input-ed" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="login-password" />
        <button className="btn-wine w-full" disabled={busy} data-testid="login-submit">{busy ? "Signing in…" : "Sign in"}</button>
        <div className="flex items-center gap-3 text-[#A8A59C] text-xs"><span className="hairline flex-1" />or<span className="hairline flex-1" /></div>
        <GoogleButton />
        <p className="text-sm text-center text-[#5E5C56]">New here? <Link to="/register" className="text-[#581C25] underline" data-testid="goto-register">Create an account</Link></p>
      </form>
    </AuthFrame>
  );
}

export function Register() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", age_confirmed: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setSession(data.user, data.token);
      toast.success("Welcome to Shufflar");
      nav("/");
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <AuthFrame title="Take a seat." sub="Book private readings, follow readers and creators, and keep a permanent archive.">
      <form onSubmit={submit} className="space-y-4">
        <input className="input-ed" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="register-name" />
        <input className="input-ed" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email" />
        <input className="input-ed" type="password" placeholder="Password (8+ characters)" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="register-password" />
        <label className="flex items-start gap-3 text-sm text-[#2C2A28]">
          <input type="checkbox" className="mt-1 accent-[#581C25]" checked={form.age_confirmed} onChange={(e) => setForm({ ...form, age_confirmed: e.target.checked })} data-testid="register-age" />
          I confirm I am 18 or older and agree to the <Link to="/legal/terms" className="underline">Terms</Link>.
        </label>
        <button className="btn-wine w-full" disabled={busy || !form.age_confirmed} data-testid="register-submit">{busy ? "Creating…" : "Create account"}</button>
        <div className="flex items-center gap-3 text-[#A8A59C] text-xs"><span className="hairline flex-1" />or<span className="hairline flex-1" /></div>
        <GoogleButton />
        <p className="text-sm text-center text-[#5E5C56]">Already a member? <Link to="/login" className="text-[#581C25] underline">Sign in</Link></p>
      </form>
    </AuthFrame>
  );
}

export function AuthCallback() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(loc.hash.replace("#", "")).get("session_id");
    if (!sid) { nav("/login"); return; }
    api.post("/auth/session", { session_id: sid })
      .then(({ data }) => { setSession(data.user, data.token); window.history.replaceState(null, "", loc.pathname); nav("/you", { replace: true }); })
      .catch(() => { toast.error("Google sign-in failed"); nav("/login", { replace: true }); });
  }, []);
  return <div className="min-h-[60vh] flex items-center justify-center"><p className="serif text-2xl animate-pulse">Opening the door…</p></div>;
}
